import { createBrowserRouter, Navigate, useParams } from "react-router-dom";
import { RequireAuth } from "./auth/RequireAuth";
import { RequireRole } from "./auth/RequireRole";
import { AppShell } from "./components/AppShell";
import { lazy, Suspense } from "react";

const SignIn = lazy(() => import("./routes/SignIn"));
const Dashboard = lazy(() => import("./routes/Dashboard"));
const AcceptInvite = lazy(() => import("./routes/AcceptInvite"));
const ResetPassword = lazy(() => import("./routes/ResetPassword"));
const CapturePage = lazy(() => import("./routes/CapturePage"));
const Drafts = lazy(() => import("./routes/Drafts"));
const Submitted = lazy(() => import("./routes/Submitted"));
const History = lazy(() => import("./routes/History"));
const OrgHistory = lazy(() => import("./routes/OrgHistory"));
const Organizations = lazy(() => import("./routes/Organizations"));
const OrganizationDetail = lazy(() => import("./routes/OrganizationDetail"));
const OrgSettings = lazy(() => import("./routes/OrgSettings"));
const UsersPage = lazy(() => import("./routes/Users"));
const Specialties = lazy(() => import("./routes/Specialties"));
const AllConsults = lazy(() => import("./routes/AllConsults"));
const AuditLogs = lazy(() => import("./routes/AuditLogs"));
const WizardShell = lazy(() =>
  import("./routes/consults/WizardShell").then((m) => ({ default: m.WizardShell })),
);
const ConsultDetail = lazy(() => import("./routes/consults/ConsultDetail"));
const OrgsLanding = lazy(() => import("./routes/OrgsLanding"));
const CuratorOrgLayoutComponent = lazy(() =>
  import("./routes/CuratorOrgLayout").then((m) => ({ default: m.CuratorOrgLayout })),
);
const PatientStep = lazy(() =>
  import("./components/consults/PatientStep").then((m) => ({ default: m.PatientStep })),
);
const ClinicalStep = lazy(() =>
  import("./components/consults/ClinicalStep").then((m) => ({ default: m.ClinicalStep })),
);
const ConfirmStep = lazy(() =>
  import("./components/consults/ConfirmStep").then((m) => ({ default: m.ConfirmStep })),
);
const SpecialistAccept = lazy(() => import("./routes/specialist/SpecialistAccept"));
const SpecialistRespond = lazy(() => import("./routes/specialist/SpecialistRespond"));
const SpecialistCompleted = lazy(() => import("./routes/specialist/SpecialistCompleted"));
const Handbook = lazy(() => import("./routes/Handbook"));
const Account = lazy(() => import("./routes/Account"));
const Support = lazy(() => import("./routes/Support"));
const PlatformSettings = lazy(() => import("./routes/PlatformSettings"));

const shell = (
  <RequireAuth>
    <AppShell />
  </RequireAuth>
);

// Old dashboard/orgs/:orgId bookmarks (pre-TEL-265) redirect to the org's own
// /organizations/:id page — a bare `<Navigate to="/organizations" />` would
// drop the reader on the generic org list instead of the org they bookmarked.
function RedirectToOrganization() {
  const { orgId } = useParams<{ orgId: string }>();
  return <Navigate to={`/organizations/${orgId}`} replace />;
}

const withSuspense = (node: React.ReactNode) => (
  <Suspense fallback={<div className="p-8 text-muted-foreground text-sm">Loading…</div>}>
    {node}
  </Suspense>
);

// Role groups for route gating. Sourced from the product authorization
// matrix (a client product decision). The frontend
// layer is defense-in-depth: the api enforces the same RBAC with 403s, and
// Postgres RLS enforces org boundaries at the DB. Mismatched-role visits
// bounce to /dashboard rather than rendering a route that would 403 every
// request anyway.
//
// All consult submitters (pcp, clinical_support_staff, consult_curator) now run
// under a single org-scoped tree at /orgs/:orgId — org identity comes from the
// URL, never me.organizationId. Single-org users (pcp/css) are redirected into
// their org by the Dashboard; curators pick an org at /orgs. practice admins
// can't create or submit consults.
const SPECIALIST_ROLES = ["specialist"] as const;
const PLATFORM_ADMIN_ROLES = ["platform_admin"] as const;
// Handbook readers (TEL-219): specialists consume it, platform admins publish it.
const HANDBOOK_ROLES = ["specialist", "platform_admin"] as const;
const PLATFORM_AND_PRACTICE_ADMIN_ROLES = ["platform_admin", "practice_admin"] as const;
// The org tree (/orgs/:orgId) is purely the submitter surface — pcp/css/curator.
// Admins are deliberately excluded: platform_admin has no org membership (so the
// org layout would bounce them anyway) and oversees via the admin surfaces
// (/consults, /organizations); practice_admin uses the admin dashboard. Keeping
// admins out of these gates preserves a clean role separation in both directions.
const ORG_VIEW_ROLES = ["pcp", "clinical_support_staff", "consult_curator"] as const;
// Who may CREATE/SUBMIT inside an org (the wizard).
const ORG_SUBMIT_ROLES = ["pcp", "clinical_support_staff", "consult_curator"] as const;
// Who may pick an org at /orgs — only multi-org users (curators).
const ORG_PICKER_ROLES = ["consult_curator"] as const;

const gated = (roles: readonly string[], node: React.ReactNode) => (
  <RequireRole roles={roles}>{node}</RequireRole>
);

export const router = createBrowserRouter([
  { path: "/sign-in", element: withSuspense(<SignIn />) },
  { path: "/accept-invite", element: withSuspense(<AcceptInvite />) },
  // TEL-206: public reset form the password-reset redeem endpoint redirects to
  // — token-gated, no auth, outside shell.
  { path: "/reset-password", element: withSuspense(<ResetPassword />) },
  // TEL-120: public phone-capture page — token-gated, no auth, outside shell.
  { path: "/capture/:token", element: withSuspense(<CapturePage />) },
  {
    path: "/",
    element: shell,
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: "dashboard", element: withSuspense(<Dashboard />) },
      // dashboard/orgs/:orgId links now go directly to /organizations/:id
      // (Dashboard rows updated in TEL-265 follow-up). Redirect old bookmarks,
      // preserving the org id — a bare redirect to the org list would drop
      // the reader on the wrong page instead of the org they bookmarked.
      {
        path: "dashboard/orgs/:orgId",
        element: <RedirectToOrganization />,
      },
      {
        path: "orgs",
        element: gated(ORG_PICKER_ROLES, withSuspense(<OrgsLanding />)),
      },
      {
        path: "orgs/:orgId",
        element: gated(ORG_VIEW_ROLES, withSuspense(<CuratorOrgLayoutComponent />)),
        children: [
          { index: true, element: withSuspense(<Dashboard />) },
          { path: "drafts", element: withSuspense(<Drafts />) },
          { path: "submitted", element: withSuspense(<Submitted />) },
          { path: "history", element: withSuspense(<History />) },
          { path: "org-history", element: withSuspense(<OrgHistory />) },
          {
            path: "consults/new",
            element: gated(ORG_SUBMIT_ROLES, withSuspense(<WizardShell />)),
            children: [
              { index: true, element: <Navigate to="patient" replace /> },
              { path: "patient", element: withSuspense(<PatientStep />) },
              { path: "clinical", element: withSuspense(<ClinicalStep />) },
              { path: "confirm", element: withSuspense(<ConfirmStep />) },
            ],
          },
          {
            path: "consults/:consultId/edit",
            element: gated(ORG_SUBMIT_ROLES, withSuspense(<WizardShell />)),
            children: [
              { index: true, element: <Navigate to="patient" replace /> },
              { path: "patient", element: withSuspense(<PatientStep />) },
              { path: "clinical", element: withSuspense(<ClinicalStep />) },
              { path: "confirm", element: withSuspense(<ConfirmStep />) },
            ],
          },
          { path: "consults/:id", element: withSuspense(<ConsultDetail />) },
        ],
      },
      // Central Queue merged into All Consults (it is now the "Needs routing"
      // view). Redirect preserves old links/bookmarks and the dashboard's
      // Unassigned tile target.
      {
        path: "central-queue",
        element: gated(
          PLATFORM_ADMIN_ROLES,
          <Navigate to="/consults?view=needs-routing" replace />,
        ),
      },
      {
        path: "consults",
        element: gated(PLATFORM_AND_PRACTICE_ADMIN_ROLES, withSuspense(<AllConsults />)),
      },
      {
        path: "organizations",
        element: gated(PLATFORM_ADMIN_ROLES, withSuspense(<Organizations />)),
      },
      {
        path: "organizations/:id",
        element: gated(PLATFORM_ADMIN_ROLES, withSuspense(<OrganizationDetail />)),
      },
      {
        path: "org-settings",
        element: gated(["practice_admin"], withSuspense(<OrgSettings />)),
      },
      {
        path: "users",
        element: gated(PLATFORM_AND_PRACTICE_ADMIN_ROLES, withSuspense(<UsersPage />)),
      },
      {
        path: "specialties",
        element: gated(PLATFORM_ADMIN_ROLES, withSuspense(<Specialties />)),
      },
      {
        path: "audit-logs",
        element: gated(PLATFORM_ADMIN_ROLES, withSuspense(<AuditLogs />)),
      },
      {
        path: "platform-settings",
        element: gated(PLATFORM_ADMIN_ROLES, withSuspense(<PlatformSettings />)),
      },
      {
        path: "account",
        element: withSuspense(<Account />),
      },
      {
        path: "support",
        element: withSuspense(<Support />),
      },
      // Submitter list + wizard routes are ONLY org-scoped now (/orgs/:orgId/*);
      // the bare equivalents were removed when pcp/css moved into the org tree.
      // Consult detail stays bare + ungated: specialists and platform_admin have
      // no org membership and can't use /orgs/:orgId/consults/:id, and the
      // acceptance email deep-links here. The page renders role-appropriate
      // content (TC-1117). Org-context callers link to the org-scoped detail.
      { path: "consults/:id", element: withSuspense(<ConsultDetail />) },
      // Specialist queue routes
      {
        path: "inbox",
        element: gated(SPECIALIST_ROLES, withSuspense(<SpecialistAccept />)),
      },
      {
        path: "respond",
        element: gated(SPECIALIST_ROLES, withSuspense(<SpecialistRespond />)),
      },
      {
        path: "completed",
        element: gated(SPECIALIST_ROLES, withSuspense(<SpecialistCompleted />)),
      },
      // Specialist handbook (TEL-219). Platform admins can also open it to
      // verify what's live — they publish it.
      {
        path: "handbook",
        element: gated(HANDBOOK_ROLES, withSuspense(<Handbook />)),
      },
      // The specialist inbox was previously named after its primary action.
      // Preserve old email links and bookmarks while keeping /inbox canonical.
      { path: "accept", element: <Navigate to="/inbox" replace /> },
      // Old specialist tabs merged into Completed (TEL-197 / Kourosh): keep the
      // paths as redirects so bookmarks and Back-links keep working.
      { path: "answered", element: <Navigate to="/completed" replace /> },
      { path: "closed", element: <Navigate to="/completed" replace /> },
    ],
  },
]);
